// @ts-nocheck
import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { DOTS } from 'config/constants';
import { useThemeName } from 'tamagui';
import { useSelector, useDispatch } from 'react-redux';
import { toggleLogin } from 'reducers/slices/loginstatus';
import {
  useInactivateSentenceForUserMutation,
  useCreateOrUpdateSentenceForUserMutation,
  useInactivateWordForUserMutation,
  useCreateOrUpdateWordForUserMutation,
  useGetRandomDictionariesQuery,
  useLazyGetRandomDictionariesQuery,
} from 'utils/RTKGraphQL';
import TradOrSimp from 'traditional-or-simplified';
import { useCreateQuizResultMutation } from 'utils/RTKGraphQL';
import { limits } from 'config/constants';
import equal from 'fast-deep-equal/es6/react';
import {
  shuffle,
  truncateArrStr,
  roundValue,
  getRandomIntInclusive,
  replaceAt,
  calculateCardWidth,
  calculateCardHeight,
  calculateFontSize,
  showToast,
} from 'utils/misc';
import last from 'lodash.last';
import { useAudio } from 'contexts/AudioContext';
import { memCardLim, pinyinSyllables } from 'config/constants';
import * as utils from 'pinyin-utils';

export function useUpdateEffect(effect: any, dependencies = []) {
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      return effect();
    }
  }, dependencies);
}

export function usePrevious(value: any) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}
export default usePrevious;

const range = (start: number, end: number) => {
  let length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

export const usePagination = (
  currentPage: number,
  totalCount: number,
  pageSize: number,
  siblingCount = 1
) => {
  const totalPageCount = Math.ceil(totalCount / pageSize);
  const paginationRange = useMemo(() => {
    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + 5;

    /*
      If the number of pages is less than the page numbers we want to show in our
      paginationComponent, we return the range [1..totalPageCount]
    */
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount
    );

    /*
      We do not want to show dots if there is only one position left 
      after/before the left/right page count as that would lead to a change if our Pagination
      component size which we do not want
    */
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount;
      let leftRange = range(1, leftItemCount);
      return [...leftRange, DOTS, totalPageCount];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount;
      let rightRange = range(
        totalPageCount - rightItemCount + 1,
        totalPageCount
      );
      return [firstPageIndex, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }
  }, [totalCount, pageSize, siblingCount, currentPage]);

  return paginationRange;
};

export function useColorModeValue(light: any, dark: any) {
  const themeName = useThemeName();
  return themeName === 'dark' ? dark : light;
}

// TODO a bit hacky, you should probably re-write how you use this hook
export const useColorModeValueOld = (lightValue, darkValue) => {
  themeName = useThemeName();
  const [colorModeValue, setColorModeValue] = useState(
    themeName === 'light' ? lightValue : darkValue
  );

  useEffect(() => {
    setColorModeValue(themeName === 'light' ? lightValue : darkValue);
  }, [themeName, lightValue, darkValue]);
  return colorModeValue;
};

export const useSentenceOperations = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile);

  const [inactivateSentenceForUser] = useInactivateSentenceForUserMutation();
  const [createOrUpdateSentenceForUser] =
    useCreateOrUpdateSentenceForUserMutation();

  const inactivateSentence = useCallback(
    async (sentence) => {
      if (!profile?.id) {
        dispatch(toggleLogin(true));
        return;
      }

      try {
        await inactivateSentenceForUser({
          userId: profile.id,
          sentenceId: sentence.id,
        }).unwrap();
      } catch (error) {
        console.error('Error inactivating sentence:', error);
      }
    },
    [profile, inactivateSentenceForUser, dispatch]
  );

  const saveSentence = useCallback(
    async (sentence) => {
      if (!profile?.id) {
        dispatch(toggleLogin(true));
        return;
      }

      try {
        await createOrUpdateSentenceForUser({
          userId: profile.id,
          sentenceId: sentence.id,
        }).unwrap();
      } catch (error) {
        console.error('Error saving sentence:', error);
      }
    },
    [profile, createOrUpdateSentenceForUser, dispatch]
  );

  return {
    inactivateSentence,
    saveSentence,
  };
};

export const useWordOperations = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile);

  const [inactivateWordForUser] = useInactivateWordForUserMutation();
  const [createOrUpdateWordForUser] = useCreateOrUpdateWordForUserMutation();

  const inactivateWord = useCallback(
    async (char) => {
      if (!profile?.id) {
        dispatch(toggleLogin(true));
        return;
      }

      try {
        await inactivateWordForUser({
          userId: profile.id,
          dictionaryId: char.id,
        }).unwrap();
      } catch (error) {
        console.error('Error inactivating word:', error);
      }
    },
    [profile, inactivateWordForUser, dispatch]
  );

  const saveWord = useCallback(
    async (char) => {
      if (!profile?.id) {
        dispatch(toggleLogin(true));
        return;
      }

      try {
        await createOrUpdateWordForUser({
          userId: profile.id,
          dictionaryId: char.id,
        }).unwrap();
      } catch (error) {
        console.error('Error saving word:', error);
      }
    },
    [profile, createOrUpdateWordForUser, dispatch]
  );

  return {
    inactivateWord,
    saveWord,
  };
};

export const useTextFilter = () => {
  const couldBePinyin = (text) => {
    const lowerText = text.toLowerCase().trim();
    return pinyinSyllables.some((syllable) => syllable.startsWith(lowerText));
  };

  const filterType = (text, mode) => {
    const cleanText = text.toLowerCase().trim();

    if (cleanText.match(/[\u3400-\u9FBF]/)) {
      return checkSimp(text);
    } else if (pinyinSyllables.includes(cleanText)) {
      return 'pinyin';
    } else if (couldBePinyin(cleanText)) {
      return mode === '文' ? 'pinyin' : 'translation';
    } else {
      return 'translation';
    }
  };

  const checkSimp = (text) => {
    // Defaults to simplified
    if (TradOrSimp.isTraditional(text)) {
      return 'traditional';
    } else {
      return 'simplified';
    }
  };

  return { filterType };
};

export const useRandomWord = () => {
  // Replace the monolithic settings with the specific slices
  const quizsettings = useSelector((state) => state.quizsettings);
  const skillsettings = useSelector((state) => state.skillsettings);

  const [trigger, { data, error, isLoading }] =
    useLazyGetRandomDictionariesQuery();

  const loadWordRandom = async (
    count = null,
    completed = null,
    additionalParams = {},
    ignoreSkill = false,
    requireOverlap = false // Add new parameter
  ) => {
    console.log('additional params', additionalParams);
    const query = {
      number: count || quizsettings.limitSelection || 1,
      ...(!ignoreSkill &&
        quizsettings.wordSelection === 0 && {
          skill: skillsettings.skillSelected + 1,
          frequency: 1,
        }),
      ...additionalParams,
      notChar: completed,
      requireOverlap: requireOverlap, // Add to query
    };

    if (isNaN(query.number) || query.number < 1) {
      throw new Error('Invalid number parameter');
    }

    const result = await trigger(query);
    return result.data;
  };

  return {
    isLoading,
    error: error ? error.message : null,
    loadWordRandom,
  };
};

export const useSavedWords = () => {
  const [savedWords, setSavedWords] = useState([]);
  const words = useSelector((state) => state.words);
  const dispatch = useDispatch();

  const removeCompleted = useCallback(
    (completed) => {
      const filteredWords = words.filter((word) => word.id !== completed.id);
      return filteredWords;
    },
    [words]
  );

  const loadSavedWords = useCallback(
    async (completed) => {
      const newWords = await removeCompleted(completed);
      setSavedWords(newWords);
      return newWords;
    },
    [removeCompleted]
  );

  return { savedWords, loadSavedWords };
};

export const useLoadWords = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Replace the monolithic settings with the specific slices
  const quizsettings = useSelector((state) => state.quizsettings);
  const { loadWordRandom } = useRandomWord();
  const { loadSavedWords } = useSavedWords();

  const loadWords = useCallback(
    async (completed = []) => {
      setIsLoading(true);
      setError(null);
      try {
        let data;
        if (
          quizsettings.wordSelection === 0 ||
          quizsettings.wordSelection === 1
        ) {
          data = await loadWordRandom(
            parseInt(limits[quizsettings.limitSelection], 10)
          ); // Ensure integer
        } else if (quizsettings.wordSelection === 2) {
          data = await loadSavedWords(completed);
          data = shuffle(data);
        }
        return data;
      } catch (err) {
        console.error('Error loading words:', err);
        setError(err);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [
      quizsettings.wordSelection,
      quizsettings.limitSelection,
      loadWordRandom,
      loadSavedWords,
    ]
  );

  return { loadWords, isLoading, error };
};

export const useRandomRules = (rules, count) => {
  const rulesStr = Array.isArray(rules) ? rules.join(',') : rules;

  const queryArg = { rulesStr, ...(count !== undefined && { count }) };
  const { data, error, isLoading, refetch } = useRandomRulesQuery(queryArg);

  return {
    randomRules: data,
    isLoading,
    error: error ? error.message : null,
    refetch,
  };
};

export const useSaveQuizResult = () => {
  const [createQuizResult] = useCreateQuizResultMutation();
  const profile = useSelector((state) => state.profile);

  const saveQuizResult = useCallback(
    async (quizData) => {
      if (profile && profile.id) {
        try {
          const input = {
            userId: profile.id,
            quizType: quizData.quizType,
            contentType: quizData.contentType,
            contentId: quizData.contentId,
            correct: quizData.correct,
            mistakes: quizData.mistakes,
            score: quizData.score,
            trials: quizData.trials || 1,
            strokes: quizData.strokes,
          };

          // Remove any undefined fields
          Object.keys(input).forEach(
            (key) => input[key] === undefined && delete input[key]
          );

          await createQuizResult(input);
        } catch (error) {
          console.error(
            `Error saving ${quizData.quizType} quiz result:`,
            error
          );
        }
      } else {
        console.warn('Cannot save quiz result: No user profile found');
      }
    },
    [profile, createQuizResult]
  );

  return saveQuizResult;
};

export const useCheckQuizFinish = () => {
  // Replace the monolithic settings with the specific slice
  const quizsettings = useSelector((state) => state.quizsettings);
  const [quizFinished, setQuizFinished] = useState(false);

  const checkQuizFinish = useCallback(
    async (responseData, completed = []) => {
      console.log('🔍 checkQuizFinish called with:', {
        responseData: responseData,
        responseDataType: typeof responseData,
        hasLength: responseData && 'length' in responseData,
        responseDataLength: responseData?.length,
        completed,
        completedLength: completed.length,
        limitSelection: quizsettings.limitSelection,
        limitValue: limits[quizsettings.limitSelection],
        parsedLimit: parseInt(limits[quizsettings.limitSelection]),
      });

      let status = false;

      if (
        !responseData ||
        !Array.isArray(responseData) ||
        responseData.length === 0
      ) {
        console.log('🛑 Quiz finished due to empty or invalid responseData');
        status = true;
      } else if (
        limits[quizsettings.limitSelection] !== 'All' &&
        completed.length >= parseInt(limits[quizsettings.limitSelection])
      ) {
        console.log('🎯 Quiz finished due to reaching limit:', {
          limit: limits[quizsettings.limitSelection],
          completedLength: completed.length,
          comparison: `${completed.length} >= ${parseInt(
            limits[quizsettings.limitSelection]
          )}`,
        });
        status = true;
      } else {
        console.log('▶️ Quiz continues:', {
          completedLength: completed.length,
          limit: parseInt(limits[quizsettings.limitSelection]),
          comparison: `${completed.length} < ${parseInt(
            limits[quizsettings.limitSelection]
          )}`,
        });
      }

      console.log('🏁 checkQuizFinish setting status to:', status);
      setQuizFinished(status);
      return status;
    },
    [quizsettings.limitSelection]
  );

  return { checkQuizFinish, quizFinished };
};

export const useWordsNeedFilled = () => {
  // Replace the monolithic settings with the specific slice
  const quizsettings = useSelector((state: any) => state.quizsettings);
  const words = useSelector((state: any) => state.words);

  return () => {
    if (quizsettings.wordSelection === 2 && words.length === 0) {
      return true;
    }
    return false;
  };
};

interface ContentItem {
  id: string;
  simplified?: string;
  traditional?: string;
  pinyin?: string;
  translation?: string;
  definition?: string;
  [key: string]: any;
}

interface WordQuizProps {
  quizType: string;
  contentType: 'word';
  questionType: string;
  answerType: string;
  useRandomContentOptions?: Record<string, any>;
  loadRandomContent: (
    count: number,
    completed: string[],
    additionalParams: Record<string, any>
  ) => Promise<ContentItem[]>;
}

export const useWordQuiz = ({
  quizType,
  contentType,
  answerType,
  useRandomContentOptions = {},
  loadRandomContent,
}: WordQuizProps) => {
  const quizstatus = useSelector((state: RootState) => state.quizstatus);

  const [content, setContent] = useState<ContentItem>({} as ContentItem);
  const [choices, setChoices] = useState<ContentItem[]>([]);
  const [completed, setCompleted] = useState<string[]>([]);
  const [currentMistakes, setCurrentMistakes] = useState(0);
  const [totalMistakes, setTotalMistakes] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const prevQuizStatus = usePrevious(quizstatus);

  const saveQuizResult = useSaveQuizResult();
  const { checkQuizFinish, quizFinished } = useCheckQuizFinish();

  const loadContent = useCallback(
    async (overrideCompleted?: string[]) => {
      const completedToUse =
        overrideCompleted !== undefined ? overrideCompleted : completed;

      console.log('📦 loadContent called with completed:', completedToUse);
      console.log('📦 loadContent - current state:', {
        totalCorrect,
        totalMistakes,
        quizDone,
        completedLength: completedToUse.length,
      });

      const data = await loadRandomContent(
        4,
        completedToUse,
        useRandomContentOptions
      );
      console.log('📦 loadContent - received data:', data?.length, 'items');

      // Use the return value from checkQuizFinish, not the state
      const isQuizFinished = await checkQuizFinish(data, completedToUse);
      console.log('📦 loadContent - checkQuizFinish returned:', isQuizFinished);

      if (isQuizFinished) {
        console.log('📦 loadContent - setting quizDone to true');
        setQuizDone(true);
        return;
      }

      const shortRes = truncateArrStr(data, answerType, 75);
      setContent(data[0]);
      setChoices(shuffle(shortRes));
      console.log('📦 loadContent - set new content and choices');
    },
    [
      loadRandomContent,
      completed,
      useRandomContentOptions,
      checkQuizFinish,
      answerType,
    ]
  );

  const handleAnswer = async (choice: ContentItem, index: number) => {
    if (equal(choice.id, content.id)) {
      const updatedCompleted = [...completed, choice.id];
      setCompleted(updatedCompleted);

      setTotalCorrect((prev) => prev + 1);
      await saveData();
      setCurrentMistakes(0);
      setContent({} as ContentItem);

      const data = await loadRandomContent(
        4,
        updatedCompleted,
        useRandomContentOptions
      );

      // Use the return value from checkQuizFinish, not the state
      const isQuizFinished = await checkQuizFinish(data, updatedCompleted);
      if (isQuizFinished) {
        setQuizDone(true);
        return;
      }

      const shortRes = truncateArrStr(data, answerType, 75);
      setContent(data[0]);
      setChoices(shuffle(shortRes));
    } else {
      if (!choices[index].wrong) {
        setChoices((prev) => {
          const newChoices = [...prev];
          newChoices[index].wrong = true;
          return newChoices;
        });
        setTotalMistakes((prev) => prev + 1);
        setCurrentMistakes((prev) => prev + 1);
      }
    }
  };

  const saveData = async () => {
    if (!content.id) return;
    const score = Math.max(0.1, 1 - currentMistakes * 0.33).toFixed(2);
    const quizData = {
      quizType,
      contentType,
      contentId: content.id,
      correct: 1,
      mistakes: currentMistakes,
      score: parseFloat(score),
    };
    await saveQuizResult(quizData);
  };

  const tryAgain = useCallback(() => {
    console.log('🔄 tryAgain function called in useWordQuiz');
    console.log('📊 Current state before reset:', {
      totalCorrect,
      totalMistakes,
      completedLength: completed.length,
      quizDone,
      quizFinished,
    });

    // Reset state
    setQuizDone(false);
    setTotalCorrect(0);
    setTotalMistakes(0);
    setContent({} as ContentItem);
    setChoices([]);
    setCompleted([]);
    console.log('♻️ State reset completed');

    // Track loadContent call - pass empty array to override stale completed state
    console.log('📥 About to call loadContent([])');
    loadContent([]) // Pass empty array instead of relying on state
      .then(() => {
        console.log('✅ loadContent completed successfully');
      })
      .catch((error) => {
        console.error('❌ loadContent failed:', error);
      });
    console.log('📤 loadContent() call initiated');
  }, [loadContent]);

  useEffect(() => {
    if (!equal(quizstatus, prevQuizStatus)) {
      tryAgain();
    }
  }, [quizstatus, prevQuizStatus, tryAgain]);

  return {
    content,
    choices,
    totalCorrect,
    totalMistakes,
    quizDone,
    handleAnswer,
    tryAgain,
  };
};

interface GrammarQuizProps {
  quizType: string;
  contentType: 'sentence';
  questionType: string;
  answerType: string;
  useRandomContentOptions?: Record<string, any>;
  loadRandomContent: (
    count: number,
    completed: string[],
    additionalParams: Record<string, any>
  ) => Promise<any>;
  loadRandomRules: (rules: string[], count: number) => Promise<any>;
}

export const useGrammarQuiz = (props: GrammarQuizProps) => {
  const {
    quizType,
    contentType,
    loadRandomContent,
    loadRandomRules,
    useRandomContentOptions = {},
    questionType,
    answerType,
  } = props;

  const quizstatus = useSelector((state: RootState) => state.quizstatus);

  const [content, setContent] = useState<any>({});
  const [choices, setChoices] = useState<any[]>([]);
  const [completed, setCompleted] = useState<string[]>([]);
  const [currentMistakes, setCurrentMistakes] = useState(0);
  const [totalMistakes, setTotalMistakes] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [correctRule, setCorrectRule] = useState<any>(null);
  const prevQuizStatus = usePrevious(quizstatus);

  const saveQuizResult = useSaveQuizResult();
  const { checkQuizFinish, quizFinished } = useCheckQuizFinish();

  const loadContent = useCallback(async () => {
    const sentenceData = await loadRandomContent(
      1,
      completed,
      useRandomContentOptions
    );

    await checkQuizFinish(sentenceData, completed);
    if (quizFinished) {
      setQuizDone(true);
      return;
    }

    const [sentence] = sentenceData;
    setContent(sentence);

    // Randomly select one rule from the sentence as the correct rule
    const _correctRule =
      sentence.rule[Math.floor(Math.random() * sentence.rule.length)];
    setCorrectRule(_correctRule);

    // Load random rules, including the correct one
    const randomRules = await loadRandomRules([_correctRule.rule], 3);
    const allRules = [...randomRules, _correctRule];
    const shortRules = truncateArrStr(allRules, answerType, 75);
    setChoices(shuffle(shortRules));
  }, [
    loadRandomContent,
    loadRandomRules,
    completed,
    useRandomContentOptions,
    checkQuizFinish,
    quizFinished,
    answerType,
  ]);

  const handleAnswer = async (choice: any, index: number) => {
    if (equal(choice.id, correctRule.id)) {
      // Correct answer
      const updatedCompleted = [...completed, content.id];
      setCompleted(updatedCompleted);

      setTotalCorrect((prev) => prev + 1);
      await saveData();
      setCurrentMistakes(0);
      setContent({});
      setCorrectRule(null);

      // Load new content
      const sentenceData = await loadRandomContent(
        1,
        updatedCompleted,
        useRandomContentOptions
      );

      // Use return value instead of state - THIS IS THE KEY FIX
      const isQuizFinished = await checkQuizFinish(
        sentenceData,
        updatedCompleted
      );
      if (isQuizFinished) {
        setQuizDone(true);
        return;
      }

      const [sentence] = sentenceData;
      setContent(sentence);

      // Randomly select one rule from the sentence as the correct rule
      const _correctRule =
        sentence.rule[Math.floor(Math.random() * sentence.rule.length)];
      setCorrectRule(_correctRule);

      // Load random rules, including the correct one
      const randomRules = await loadRandomRules([_correctRule.rule], 3);
      const allRules = [...randomRules, _correctRule];
      const shortRules = truncateArrStr(allRules, answerType, 75);
      setChoices(shuffle(shortRules));
    } else {
      // Incorrect answer
      if (!choices[index].wrong) {
        setChoices((prev) => {
          const newChoices = [...prev];
          newChoices[index].wrong = true;
          return newChoices;
        });
        setTotalMistakes((prev) => prev + 1);
        setCurrentMistakes((prev) => prev + 1);
      }
    }
  };

  const saveData = async () => {
    if (!content.id) return;
    const score = Math.max(0.1, 1 - currentMistakes * 0.33).toFixed(2);
    const quizData = {
      quizType,
      contentType,
      contentId: content.id,
      correct: 1,
      mistakes: currentMistakes,
      score: parseFloat(score),
    };
    await saveQuizResult(quizData);
  };

  const tryAgain = useCallback(() => {
    setQuizDone(false);
    setTotalCorrect(0);
    setTotalMistakes(0);
    setContent({});
    setChoices([]);
    setCompleted([]);
    setCorrectRule(null);
    loadContent();
  }, [loadContent]);

  useEffect(() => {
    if (!equal(quizstatus, prevQuizStatus)) {
      tryAgain();
    }
  }, [quizstatus, prevQuizStatus, tryAgain]);

  // Initial load effect
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  return {
    content,
    choices,
    totalCorrect,
    totalMistakes,
    quizDone,
    handleAnswer,
    tryAgain,
  };
};

interface StackItem {
  color: string;
  text: string;
  status?: string;
}

interface Word {
  id: string;
  simplified: string;
  traditional: string;
  [key: string]: string;
}

const tatoebaBase = 'https://tatoeba.org/en/sentences/show/';
const punctuation = ['。', '?', '!', '？', '.', ',', "'", ';', ':', '，', '！'];

interface ClozeQuizProps {
  loadRandomContent: (
    number: number,
    completed: string[],
    options: any
  ) => Promise<Sentence[]>;
  useRandomContentOptions?: any;
}

export const useClozeQuiz = ({
  loadRandomContent,
  useRandomContentOptions = {},
}: ClozeQuizProps) => {
  const languagesettings = useSelector(
    (state: RootState) => state.languagesettings
  );
  const quizsettings = useSelector((state: RootState) => state.quizsettings);
  const quizstatus = useSelector((state: RootState) => state.quizstatus);
  const quizType: 'traditional' | 'simplified' = languagesettings.traditional
    ? 'traditional'
    : 'simplified';

  const [sentence, setSentence] = useState<Sentence>({} as Sentence);
  const [finalAnswerList, setFinalAnswerList] = useState<Answer[]>([]);
  const [completed, setCompleted] = useState<string[]>([]);
  const [currentMistakes, setCurrentMistakes] = useState<number>(0);
  const [totalMistakes, setTotalMistakes] = useState<number>(0);
  const [totalCorrect, setTotalCorrect] = useState<number>(0);
  const [quizDone, setQuizDone] = useState<boolean>(false);
  const [source, setSource] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const prevQuizStatus = usePrevious(quizstatus);
  const saveQuizResult = useSaveQuizResult();
  const { checkQuizFinish, quizFinished } = useCheckQuizFinish();
  const { unloadSound, loadSound } = useAudio();

  const calcSentLen = (_sentence: string): number => {
    let sentenceLength = _sentence.length - 1;
    if (punctuation.includes(last(_sentence) as string)) {
      sentenceLength = sentenceLength - 1;
    }
    return sentenceLength;
  };

  const removeChars = (sentence_obj: Sentence): Answer[] => {
    let _sentence = sentence_obj[quizType];
    let sentenceLength = calcSentLen(_sentence);
    let randomPos = getRandomIntInclusive(0, sentenceLength);
    let randomLen = getRandomIntInclusive(1, sentenceLength - randomPos);
    if (randomLen > Math.round(sentenceLength / 2)) {
      randomLen = Math.round(sentenceLength / 2);
    }
    let charRemain = '';
    for (let i = randomPos; i < randomPos + randomLen; i++) {
      charRemain += _sentence[i];
      _sentence = replaceAt(_sentence, i, '_');
    }

    _sentence = _sentence.replace('_', '___');

    const updatedSentence = {
      ...sentence_obj,
      [quizType]: _sentence,
    };

    setSentence(updatedSentence);
    return [{ chars: charRemain, correct: true }];
  };

  const loadContent = useCallback(async () => {
    setIsLoading(true);

    try {
      const data = await loadRandomContent(
        4,
        completed,
        useRandomContentOptions
      );

      await checkQuizFinish(data, completed);
      if (quizFinished) {
        setQuizDone(true);
        return;
      }

      await loadSound(data[0][quizType]);
      const correctAnswer = removeChars(data[0]);
      setSource(tatoebaBase + data[0].cmn_tatoeba_id);

      const incorrectAnswers = data
        .slice(1)
        .map((w) => ({ chars: w[quizType], correct: false }));
      const allAnswers = incorrectAnswers.concat(correctAnswer);
      setFinalAnswerList(shuffle(allAnswers));
    } finally {
      setIsLoading(false);
    }
  }, [
    loadRandomContent,
    completed,
    useRandomContentOptions,
    checkQuizFinish,
    quizFinished,
    loadSound,
    quizType,
  ]);

  const handleAnswer = useCallback(
    async (answer: Answer, index: number) => {
      await unloadSound();
      if (answer.correct) {
        const newTotalCorrect = totalCorrect + 1;
        setTotalCorrect(newTotalCorrect);

        // Direct check of the count here
        if (
          limits[quizsettings.limitSelection] !== 'All' &&
          newTotalCorrect >= parseInt(limits[quizsettings.limitSelection])
        ) {
          setQuizDone(true);
          return;
        }

        setFinalAnswerList([]);
        await saveData();
        setCompleted((prev) => [...prev, sentence.id]);
        setCurrentMistakes(0);
        setSentence({} as Sentence);
        setIsLoading(true);
        loadContent();
      } else {
        // Incorrect answer handling remains the same
        if (!finalAnswerList[index].wrong) {
          setFinalAnswerList((prev) => {
            const newList = [...prev];
            newList[index].wrong = true;
            return newList;
          });
          setTotalMistakes((prev) => prev + 1);
          setCurrentMistakes((prev) => prev + 1);
        }
      }
    },
    [
      unloadSound,
      saveData,
      sentence,
      loadContent,
      finalAnswerList,
      totalCorrect,
      quizsettings.limitSelection,
    ]
  );

  const saveData = useCallback(async () => {
    if (!sentence.id) return;
    const score = Math.max(0.1, 1 - currentMistakes * 0.33).toFixed(2);
    const quizData = {
      quizType: 'cloze',
      contentType: 'sentence',
      contentId: sentence.id,
      correct: 1,
      mistakes: currentMistakes,
      score: parseFloat(score),
    };
    await saveQuizResult(quizData);
  }, [sentence.id, currentMistakes, saveQuizResult]);

  const tryAgain = useCallback(async () => {
    // First set quiz done to false
    setQuizDone(false);

    // Reset all state
    setTotalCorrect(0);
    setTotalMistakes(0);
    setSentence({} as Sentence);
    setFinalAnswerList([]);
    setCompleted([]);
    setIsLoading(true);

    // Small delay to ensure state is updated before loading new content
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Load new content
    await loadContent();
  }, [loadContent]);

  useEffect(() => {
    if (!equal(quizstatus, prevQuizStatus)) {
      tryAgain();
    }
  }, [quizstatus, prevQuizStatus, tryAgain]);

  useEffect(() => {
    const initialLoad = async () => {
      await loadContent();
    };
    initialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once on mount

  return {
    sentence,
    finalAnswerList,
    totalMistakes,
    totalCorrect,
    quizDone,
    source,
    quizType,
    isLoading,
    handleAnswer,
    tryAgain,
  };
};

const r = 1;
const maxR = 10;

interface StackItem {
  color: string;
  text: string;
  status?: string;
}

interface Word {
  id: string;
  simplified: string;
  traditional: string;
  [key: string]: string;
}

export const useChengyu = () => {
  const languagesettings = useSelector((state: any) => state.languagesettings);
  const quizsettings = useSelector((state: any) => state.quizsettings);
  const words = useSelector((state: any) => state.words);

  const [c, setC] = useState<number>(0);
  const [stackArr, setStackArr] = useState<StackItem[][]>([]);
  const [currentWordInput, setCurrentWordInput] = useState<string>('');
  const [word, setWord] = useState<Word>({
    id: '',
    simplified: '',
    traditional: '',
  });
  const [currentRow, setCurrentRow] = useState<number>(0);
  const [completed, setCompleted] = useState<string[]>([]);
  const [quizDone, setQuizDone] = useState<boolean>(false);
  const [answerColor, setAnswerColor] = useState<string>('transparent');
  const [quizFinishText, setQuizFinishText] = useState<string>('');
  const [wordsNeedFilled, setWordsNeedFilled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const colorMode = useThemeName();
  const quizType: 'traditional' | 'simplified' = languagesettings.traditional
    ? 'traditional'
    : 'simplified';

  const { loadWordRandom } = useRandomWord();
  const { loadSavedWords } = useSavedWords();
  const saveQuizResult = useSaveQuizResult();

  const generateEmptyArr = useCallback((): StackItem[][] => {
    const arr: StackItem[][] = [];
    for (let i = 0; i < r; i++) {
      arr.push(new Array(c).fill({ color: '#FFF', text: '' }));
    }
    return arr;
  }, [c]);

  const loadWords = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      let data: Word[] = [];
      if (quizsettings.wordSelection === 0) {
        data = await loadWordRandom(
          1,
          completed,
          { minChars: 4, maxChars: 4, idiom: true },
          true,
          true // Add requireOverlap
        );
      } else if (quizsettings.wordSelection === 1) {
        data = await loadWordRandom(
          1,
          completed,
          {
            idiom: true,
            minChars: 4,
            maxChars: 4,
          },
          true,
          true // Add requireOverlap
        );
      } else if (quizsettings.wordSelection === 2 && words.length > 0) {
        data = await loadSavedWords(completed);
        data = shuffle(data);
        data = data.slice(0, 1);

        // Filter saved words for 4-character entries if needed
        if (
          data.length > 0 &&
          data[0][quizType] &&
          data[0][quizType].length !== 4
        ) {
          const filtered = words.filter(
            (w) => w[quizType] && w[quizType].length === 4
          );
          if (filtered.length > 0) {
            data = [shuffle(filtered)[0]];
          }
        }
      } else if (quizsettings.wordSelection === 2 && words.length === 0) {
        setWordsNeedFilled(true);
        return;
      }

      if (data && data.length > 0 && data[0][quizType]) {
        setC(data[0][quizType].length);
        setWord(data[0]);
        setWordsNeedFilled(false);
      } else {
        console.error('No valid word data received');
        setWordsNeedFilled(true);
      }
    } catch (err) {
      console.error('Error loading words:', err);
      setError(
        err instanceof Error ? err : new Error('Unknown error occurred')
      );
      setWordsNeedFilled(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    quizsettings.wordSelection,
    words,
    completed,
    quizType,
    loadWordRandom,
    loadSavedWords,
  ]);

  const checkCorrect = useCallback((): boolean => {
    let allSuccess = true;
    for (let i = 0; i < stackArr[currentRow].length; i++) {
      if (stackArr[currentRow][i].status !== 'correct') {
        allSuccess = false;
      }
    }
    return allSuccess;
  }, [stackArr, currentRow]);

  const completeQuiz = useCallback(async (): Promise<void> => {
    // Changed from word['simplified'] to word.id
    let _completed = [...completed, word.id];
    setCompleted(_completed);
    setQuizDone(true);
    let success = checkCorrect();
    if (!success) {
      setAnswerColor(colorMode === 'light' ? '$pred500' : '$pred500');
      setQuizFinishText('You failed! This is the word:');
    } else {
      setAnswerColor(colorMode === 'light' ? 'green.400' : 'green.600');
      setQuizFinishText('You succeeded! This is the word:');
    }

    let _score = 1 - (stackArr.length - 1) / maxR;
    _score = roundValue(Math.max(_score, 0));

    const quizData = {
      quizType: 'chengle',
      contentType: 'word',
      contentId: word.id,
      correct: 1,
      mistakes: stackArr.length - 1,
      score: _score,
    };

    await saveQuizResult(quizData);
  }, [completed, word, stackArr, colorMode, checkCorrect, saveQuizResult]);

  const processAnswer = useCallback(
    (currentWordInputArr: string[], _stackArr: StackItem[][]) => {
      const wordArr = word[quizType].split('');
      for (let i = 0; i < wordArr.length; i++) {
        let wordChar = wordArr[i];
        for (let x = 0; x < currentWordInputArr.length; x++) {
          let wordInputChar = currentWordInputArr[x];
          if (wordChar === wordInputChar) {
            if (i === x) {
              _stackArr[currentRow][x] = {
                ..._stackArr[currentRow][x],
                color: colorMode === 'light' ? 'green.400' : 'green.600',
                status: 'correct',
              };
            } else if (_stackArr[currentRow][x].status !== 'correct') {
              _stackArr[currentRow][x] = {
                ..._stackArr[currentRow][x],
                color: colorMode === 'light' ? 'yellow.400' : 'yellow.500',
                status: 'exists',
              };
            }
          }

          // Debug logging
          console.log('Current Row:', currentRow);
          console.log('x value:', x);
          console.log('Stack Array state:', _stackArr);
          console.log('Current row array:', _stackArr[currentRow]);
          if (_stackArr[currentRow]) {
            console.log('Element at x:', _stackArr[currentRow][x]);
          }

          if (
            i !== x &&
            _stackArr[currentRow][x].status !== 'correct' &&
            _stackArr[currentRow][x].status !== 'exists'
          ) {
            _stackArr[currentRow][x] = {
              ..._stackArr[currentRow][x],
              color: colorMode === 'light' ? 'gray.200' : 'gray.600',
              status: 'fail',
            };
          }
        }
      }
      return _stackArr;
    },
    [word, quizType, currentRow, colorMode]
  );

  const incrementStackArr = useCallback((): void => {
    const currentWordInputArr = currentWordInput.split('');
    let _stackArr = processAnswer(currentWordInputArr, [...stackArr]);

    for (let i = 0; i < currentWordInputArr.length; i++) {
      _stackArr[currentRow][i] = {
        ..._stackArr[currentRow][i],
        text: currentWordInputArr[i],
      };
    }

    if (_stackArr.length === maxR || checkCorrect()) {
      completeQuiz();
    } else if (_stackArr.length < maxR) {
      _stackArr.push(
        new Array(c).fill({ color: '#FFF', text: '', status: 'empty' })
      );
    }

    setStackArr(_stackArr);
    setCurrentWordInput('');
    setCurrentRow(currentRow + 1);
  }, [
    currentWordInput,
    stackArr,
    currentRow,
    c,
    processAnswer,
    checkCorrect,
    completeQuiz,
  ]);

  const tryAgain = useCallback((): void => {
    setQuizFinishText('');
    setCurrentRow(0);
    setStackArr(generateEmptyArr());
    loadWords();
    setQuizDone(false);
  }, [generateEmptyArr, loadWords]);

  useEffect(() => {
    loadWords();
  }, []); // Empty dependency array to run only once on mount

  useEffect(() => {
    if (c > 0) {
      setStackArr(generateEmptyArr());
    }
  }, [c, generateEmptyArr]);

  return {
    c,
    stackArr,
    currentWordInput,
    setCurrentWordInput,
    word,
    currentRow,
    quizDone,
    answerColor,
    quizFinishText,
    wordsNeedFilled,
    quizType,
    incrementStackArr,
    tryAgain,
    isLoading,
    error,
  };
};

let percentageWidth = 0.08;
let minWidth = 80;
let percentageWidthHeight = 0.08;
let minHeight = minWidth * 1.12;
let percentageWidthFont = 0.015;
let minFont = 16;

export const useMemoryMatch = () => {
  // Replace monolithic settings with specific slices
  const languagesettings = useSelector((state) => state.languagesettings);
  const quizsettings = useSelector((state) => state.quizsettings);
  const profile = useSelector((state) => state.profile);
  const quizstatus = useSelector((state) => state.quizstatus);
  const words = useSelector((state) => state.words);

  const [items, setItems] = useState([]);
  const [itemWidth, setItemWidth] = useState(
    calculateCardWidth(percentageWidth, minWidth)
  );
  const [itemHeight, setItemHeight] = useState(
    calculateCardHeight(percentageWidthHeight, minHeight)
  );
  const [fontSize, setFontSize] = useState(
    calculateFontSize(percentageWidthFont, minFont)
  );
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalMistakes, setTotalMistakes] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [wordsNeedFilled, setWordsNeedFilled] = useState(false);
  const prevQuizStatus = usePrevious(quizstatus);
  const char_type = languagesettings.traditional ? 'traditional' : 'simplified';

  const { loadWords, isLoading, error } = useLoadWords();
  const saveQuizResult = useSaveQuizResult();

  const saveData = useCallback(
    async (state, word_id_one, word_id_two) => {
      if (profile) {
        const quizData = {
          quizType: 'memory',
          contentType: 'dictionary',
          contentId: word_id_one,
          correct: state === 'correct' ? 2 : 0,
          mistakes: state === 'wrong' ? 1 : 0,
          score: state === 'correct' ? 1 : 0,
        };
        await saveQuizResult(quizData);

        if (state === 'wrong') {
          quizData.contentId = word_id_two;
          await saveQuizResult(quizData);
        }
      }
    },
    [profile, saveQuizResult]
  );

  const processWords = useCallback(
    (words) => {
      let word_cards = [];
      for (let i = 0; i < words.length; i++) {
        let char = {
          body: words[i][char_type],
          state: 'default',
          char_id: words[i].id,
          id: 'char_' + words[i].id,
        };
        let pinyin = {
          body: words[i].pinyinMarks,
          state: 'default',
          char_id: words[i].id,
          id: 'pinyin_' + words[i].id,
        };
        word_cards.push(char);
        word_cards.push(pinyin);
      }
      return shuffle(word_cards);
    },
    [char_type]
  );

  const loadMemoryWords = useCallback(async () => {
    const data = await loadWords();
    if (data.length === 0) {
      setWordsNeedFilled(true);
      return;
    }

    if (wordsNeedFilled === true) {
      setWordsNeedFilled(false);
    }

    const processedWords = processWords(
      data.slice(0, memCardLim[quizsettings.limitSelection])
    );
    setItems(processedWords);
  }, [loadWords, quizsettings.limitSelection, processWords]);

  // Rest of the hook remains the same
  const resize = useCallback(() => {
    setItemWidth(calculateCardWidth(percentageWidth, minWidth));
    setItemHeight(calculateCardHeight(percentageWidthHeight, minHeight));
    setFontSize(calculateFontSize(percentageWidthFont, minFont));
  }, []);

  const tryAgain = useCallback(async () => {
    setTotalCorrect(0);
    setTotalMistakes(0);
    setQuizDone(false);
    loadMemoryWords();
  }, [loadMemoryWords]);

  const clickTile = useCallback(
    async (item) => {
      let _items = [...items];
      let selected = _items.find((i) => i.state === 'selected');
      if (item.state === 'correct') {
        return;
      } else if (typeof selected === 'undefined') {
        let newItem = _items.find((i) => i.id === item.id);
        let index = _items.indexOf(newItem);
        newItem.state = 'selected';
        _items[index] = newItem;
        setItems(_items);
      } else if (selected.id === item.id) {
        await setOneTile('default', _items, item);
      } else if (selected.char_id !== item.char_id) {
        await setBothTiles('wrong', _items, selected, item);
        setTotalMistakes((prev) => prev + 1);
      } else if (selected.char_id === item.char_id) {
        await setBothTiles('correct', _items, selected, item);
        setTotalCorrect((prev) => prev + 1);
        await checkAll();
      }
    },
    [items, setOneTile, setBothTiles, checkAll]
  );

  const checkAll = useCallback(() => {
    let correctArr = items.filter((item) => item.state === 'correct');
    let numCorr = correctArr.length;
    if (numCorr === items.length) {
      setQuizDone(true);
    }
  }, [items]);

  const setWrongRight = useCallback(async () => {
    let _items = items.map((item) => {
      if (item.state === 'wrong') {
        item.state = 'default';
      }
      return item;
    });
    setItems(_items);
  }, [items]);

  const setOneTile = useCallback(async (state, _items, item) => {
    let newItem = _items.find((i) => i.id === item.id);
    let index = _items.indexOf(newItem);
    newItem.state = state;
    _items[index] = newItem;
    setItems(_items);
  }, []);

  const setBothTiles = useCallback(
    async (state, _items, selected, item) => {
      saveData(state, item.char_id, selected.char_id);
      let newItem = _items.find((i) => i.id === item.id);
      let newItemIndex = _items.indexOf(newItem);
      let selectedItemIndex = _items.indexOf(selected);
      newItem.state = state;
      selected.state = state;
      _items[newItemIndex] = newItem;
      _items[selectedItemIndex] = selected;
      setItems(_items);
    },
    [saveData]
  );

  const calculateColor = useCallback((item) => {
    let color = '#9ca3af';
    if (item.state === 'selected') {
      color = 'black';
    } else if (item.state === 'wrong') {
      color = 'red';
    } else if (item.state === 'correct') {
      color = 'green';
    }
    return color;
  }, []);

  useEffect(() => {
    if (window.addEventListener) {
      window.addEventListener('resize', resize);
    }
    loadMemoryWords();

    return () => {
      if (window.removeEventListener) {
        window.removeEventListener('resize', resize);
      }
    };
  }, []);

  useEffect(() => {
    if (!equal(quizstatus, prevQuizStatus)) {
      tryAgain();
    }
  }, [quizstatus, prevQuizStatus, tryAgain]);

  return {
    items,
    itemWidth,
    itemHeight,
    fontSize,
    totalCorrect,
    totalMistakes,
    quizDone,
    wordsNeedFilled,
    isLoading,
    error,
    clickTile,
    tryAgain,
    setWrongRight,
    calculateColor,
  };
};

interface Word {
  id: number;
  simplified: string;
  pinyinNumbers: string;
  pinyinMarks: string;
  translation: string;
  level: number;
  traditional: string;
  dictFreq: number;
  fromDictionary: any[];
  components: any[];
  disabled: boolean;
  frequency: number;
  simptradsame: boolean;
  pos: string;
}

interface Pinyin {
  tone: string;
  wrong?: boolean;
}

const toneRandomizer = (pinyin: string): string => {
  const regexp = /[0-9]/g;
  const pinyins = [...pinyin.matchAll(regexp)];
  for (let i = 0; i < pinyins.length; i++) {
    let index = pinyins[i].index!;
    let randomNum = getRandomIntInclusive(1, 5);
    pinyin = replaceAt(pinyin, index, randomNum.toString());
  }
  let pinyin_items = pinyin.split(' ');
  pinyin_items = pinyin_items.map((item) => utils.numberToMark(item));
  pinyin = pinyin_items.join(' ');
  return pinyin;
};

export const useTones = ({
  loadRandomContent,
  useRandomContentOptions = {},
}) => {
  // Replace monolithic settings with specific slices
  const languagesettings = useSelector((state) => state.languagesettings);
  const quizsettings = useSelector((state) => state.quizsettings);
  const quizstatus = useSelector((state) => state.quizstatus);

  // State declarations
  const [word, setWord] = useState({});
  const [pinyins, setPinyins] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [currentMistakes, setCurrentMistakes] = useState(0);
  const [totalMistakes, setTotalMistakes] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [wordsNeedFilled, setWordsNeedFilled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const quizType = languagesettings.traditional ? 'traditional' : 'simplified';
  const prevQuizStatus = usePrevious(quizstatus);

  const saveQuizResult = useSaveQuizResult();
  const { checkQuizFinish, quizFinished } = useCheckQuizFinish();
  const { loadSoundWord, unloadSound, speak } = useAudio();

  const loadContent = useCallback(
    async (overrideCompleted?: string[]) => {
      const completedToUse =
        overrideCompleted !== undefined ? overrideCompleted : completed;
      setIsLoading(true);

      try {
        // Check if we've hit the limit before requesting more data
        if (
          limits[quizsettings.limitSelection] !== 'All' &&
          completedToUse.length >= parseInt(limits[quizsettings.limitSelection])
        ) {
          setQuizDone(true);
          return;
        }

        const data = await loadRandomContent(
          4,
          completedToUse,
          useRandomContentOptions
        );

        if (!data || data.length === 0) {
          setQuizDone(true);
          return;
        }

        // Use the return value from checkQuizFinish, not the state
        const isQuizFinished = await checkQuizFinish(data, completedToUse);
        if (isQuizFinished) {
          setQuizDone(true);
          return;
        }

        await loadSoundWord(data[0].simplified);

        setWord(data[0]);

        // Generate tone options
        const correctTone = data[0].pinyinMarks;
        let toneChoices = [
          { tone: correctTone, correct: true },
          { tone: toneRandomizer(data[0].pinyinNumbers), correct: false },
          { tone: toneRandomizer(data[0].pinyinNumbers), correct: false },
          { tone: toneRandomizer(data[0].pinyinNumbers), correct: false },
        ];

        setPinyins(shuffle(toneChoices));
      } finally {
        setIsLoading(false);
      }
    },
    [
      loadRandomContent,
      completed,
      useRandomContentOptions,
      checkQuizFinish,
      quizFinished,
      loadSoundWord,
      quizType,
      quizsettings.limitSelection,
    ]
  );

  // Rest of the hook remains the same, just updating dependencies
  const saveData = useCallback(async () => {
    if (!word.id) return;
    let score = Math.max(0.1, 1 - currentMistakes * 0.33);
    score = parseFloat(score.toFixed(2));

    const quizData = {
      quizType: 'tone',
      contentType: 'word',
      contentId: word.id,
      correct: 1,
      mistakes: currentMistakes,
      score: score,
    };

    await saveQuizResult(quizData);
  }, [currentMistakes, word.id, saveQuizResult]);

  const handleAnswer = useCallback(
    async (pinyin, index) => {
      if (equal(pinyin.tone, word.pinyinMarks)) {
        // Correct answer
        await saveData();
        const updatedCompleted = [...completed, word.id];
        setCompleted(updatedCompleted);
        setTotalCorrect((prev) => prev + 1);
        setCurrentMistakes(0);
        setWord({});

        await unloadSound();
        setIsLoading(true);

        try {
          // Check if we've hit the limit before requesting more data
          if (
            limits[quizsettings.limitSelection] !== 'All' &&
            updatedCompleted.length >=
              parseInt(limits[quizsettings.limitSelection])
          ) {
            setQuizDone(true);
            return;
          }

          const data = await loadRandomContent(
            4,
            updatedCompleted,
            useRandomContentOptions
          );

          if (!data || data.length === 0) {
            setQuizDone(true);
            return;
          }

          // Use return value instead of state - THIS IS THE KEY FIX
          const isQuizFinished = await checkQuizFinish(data, updatedCompleted);
          if (isQuizFinished) {
            setQuizDone(true);
            return;
          }

          await loadSoundWord(data[0].simplified);
          setWord(data[0]);

          // Generate tone options
          const correctTone = data[0].pinyinMarks;
          let toneChoices = [
            { tone: correctTone, correct: true },
            { tone: toneRandomizer(data[0].pinyinNumbers), correct: false },
            { tone: toneRandomizer(data[0].pinyinNumbers), correct: false },
            { tone: toneRandomizer(data[0].pinyinNumbers), correct: false },
          ];

          setPinyins(shuffle(toneChoices));
        } finally {
          setIsLoading(false);
        }
      } else {
        // Incorrect answer
        if (!pinyins[index].wrong) {
          setPinyins((prev) => {
            const newPinyins = [...prev];
            newPinyins[index].wrong = true;
            return newPinyins;
          });
          setTotalMistakes((prev) => prev + 1);
          setCurrentMistakes((prev) => prev + 1);
        }
      }
    },
    [
      word,
      completed,
      pinyins,
      saveData,
      unloadSound,
      loadRandomContent,
      useRandomContentOptions,
      checkQuizFinish,
      quizsettings.limitSelection,
      loadSoundWord,
    ]
  );

  const tryAgain = useCallback(() => {
    setQuizDone(false);
    setTotalCorrect(0);
    setTotalMistakes(0);
    setWord({});
    setPinyins([]);
    setCompleted([]);
    setIsLoading(true);
    // Pass empty array to override stale completed state
    loadContent([]);
  }, [loadContent]);

  useEffect(() => {
    if (!equal(quizstatus, prevQuizStatus)) {
      tryAgain();
    }
  }, [quizstatus, prevQuizStatus, tryAgain]);

  useEffect(() => {
    loadContent();
  }, []);

  return {
    word,
    pinyins,
    quizType,
    totalCorrect,
    totalMistakes,
    quizDone,
    wordsNeedFilled,
    isLoading,
    speak,
    handleAnswer,
    tryAgain,
  };
};

interface Word {
  id: number;
  simptradsame: boolean;
}

interface RootState {
  settings: {
    traditional: boolean;
    wordSelection: number;
  };
  profile: any;
  quizstatus: any;
  words: any[];
}

export const useTradSimpleQuiz = () => {
  const dispatch = useDispatch();
  const languagesettings = useSelector(
    (state: RootState) => state.languagesettings
  );
  const quizsettings = useSelector((state: RootState) => state.quizsettings);
  const profile = useSelector((state: RootState) => state.profile);
  const quizstatus = useSelector((state: RootState) => state.quizstatus);
  const words = useSelector((state: RootState) => state.words);

  const [word, setWord] = useState<Word>({} as Word);
  const [wordQueue, setWordQueue] = useState<Word[]>([]);
  const [completed, setCompleted] = useState<number[]>([]);
  const [totalMistakes, setTotalMistakes] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [wordsNeedFilled, setWordsNeedFilled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingAnswer, setIsProcessingAnswer] = useState(false);
  const quizType = languagesettings.traditional ? 'traditional' : 'simplified';
  const prevQuizStatus = usePrevious(quizstatus);

  const { loadWordRandom } = useRandomWord();
  const { loadSavedWords } = useSavedWords();
  const saveQuizResult = useSaveQuizResult();
  const { checkQuizFinish } = useCheckQuizFinish();

  // Calculate batch size based on quiz settings
  const getBatchSize = () => {
    const limitSetting = limits[quizsettings.limitSelection];
    if (limitSetting === 'All') {
      // For "All", load a reasonable chunk size and keep loading more as needed
      return 50; // Load 50 at a time for "All" mode
    } else {
      // For specific limits (5, 10, 20, etc.), load exactly that many
      const limit = parseInt(limitSetting);
      return Math.max(limit, 10); // Minimum of 10 to avoid too frequent loads
    }
  };

  // Load a batch of words based on quiz settings
  const loadWordBatch = async (completedArray) => {
    setIsLoading(true);

    try {
      let data: Word[];
      const batchSize = getBatchSize();

      if (
        quizsettings.wordSelection === 0 ||
        quizsettings.wordSelection === 1
      ) {
        data = await loadWordRandom(batchSize, completedArray);
      } else if (quizsettings.wordSelection === 2 && words.length > 0) {
        data = await loadSavedWords(completedArray);
        data = shuffle(data);
        // For saved words, take the batch size or remaining words
        data = data.slice(0, batchSize);
      } else if (quizsettings.wordSelection === 2 && words.length === 0) {
        setWordsNeedFilled(true);
        setIsLoading(false);
        return;
      }

      if (wordsNeedFilled === true) {
        setWordsNeedFilled(false);
      }

      // Check if quiz should finish
      const isQuizFinished = await checkQuizFinish(data, completedArray);
      if (isQuizFinished) {
        setQuizDone(true);
        setIsLoading(false);
        return;
      }

      // Set up the word queue and current word
      if (data && data.length > 0) {
        setWordQueue(data.slice(1)); // Queue is all words except the first
        setWord(data[0]); // Current word is the first one
      }
    } catch (error) {
      console.error('Error loading words:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Get next word from queue
  const getNextWord = async (updatedCompleted) => {
    if (wordQueue.length > 0) {
      // Use next word from queue
      const nextWord = wordQueue[0];
      const remainingQueue = wordQueue.slice(1);

      setWord(nextWord);
      setWordQueue(remainingQueue);

      // If queue is getting low AND we're in "All" mode, preload more words
      const limitSetting = limits[quizsettings.limitSelection];
      if (limitSetting === 'All' && remainingQueue.length <= 5) {
        // Don't await this - let it load in background
        loadWordBatch(updatedCompleted);
      }
    } else {
      // Queue is empty, need to load new batch
      // This should rarely happen unless we're at the end or in "All" mode
      await loadWordBatch(updatedCompleted);
    }
  };

  const saveData = async (state: string) => {
    let mistakes = 0;
    let correct = 0;

    if (state === 'wrong') {
      mistakes = 1;
    } else if (state === 'correct') {
      correct = 1;
    }

    const quizData = {
      quizType: 'tradsimple',
      contentType: 'word',
      contentId: word.id,
      correct: correct,
      mistakes: mistakes,
      score: correct / (correct + mistakes),
    };

    await saveQuizResult(quizData);
  };

  const showStatus = (message: string) => {
    showToast(
      message.toUpperCase(),
      message === 'correct' ? 'success' : 'error',
      500
    );
  };

  const equals = async () => {
    if (isProcessingAnswer || !word.id) {
      return;
    }

    setIsProcessingAnswer(true);

    try {
      const isCorrect = word.simptradsame;
      const state = isCorrect ? 'correct' : 'wrong';

      // Update counters
      if (isCorrect) {
        setTotalCorrect((prev) => prev + 1);
      } else {
        setTotalMistakes((prev) => prev + 1);
      }

      // Save the result
      await saveData(state);
      showStatus(state);

      // Update completed list
      const updatedCompleted = [...completed, word.id];
      setCompleted(updatedCompleted);

      // Check if quiz should finish before getting next word
      const limitSetting = limits[quizsettings.limitSelection];
      if (
        limitSetting !== 'All' &&
        updatedCompleted.length >= parseInt(limitSetting)
      ) {
        setQuizDone(true);
        return;
      }

      // Get next word (usually instant from queue)
      await getNextWord(updatedCompleted);
    } finally {
      setIsProcessingAnswer(false);
    }
  };

  const notEquals = async () => {
    if (isProcessingAnswer || !word.id) {
      return;
    }

    setIsProcessingAnswer(true);

    try {
      const isCorrect = !word.simptradsame;
      const state = isCorrect ? 'correct' : 'wrong';

      // Update counters
      if (isCorrect) {
        setTotalCorrect((prev) => prev + 1);
      } else {
        setTotalMistakes((prev) => prev + 1);
      }

      // Save the result
      await saveData(state);
      showStatus(state);

      // Update completed list
      const updatedCompleted = [...completed, word.id];
      setCompleted(updatedCompleted);

      // Check if quiz should finish before getting next word
      const limitSetting = limits[quizsettings.limitSelection];
      if (
        limitSetting !== 'All' &&
        updatedCompleted.length >= parseInt(limitSetting)
      ) {
        setQuizDone(true);
        return;
      }

      // Get next word (usually instant from queue)
      await getNextWord(updatedCompleted);
    } finally {
      setIsProcessingAnswer(false);
    }
  };

  const tryAgain = () => {
    setQuizDone(false);
    setTotalCorrect(0);
    setTotalMistakes(0);
    setWord({} as Word);
    setWordQueue([]);
    setCompleted([]);
    setWordsNeedFilled(false);
    setIsProcessingAnswer(false);
    // Load fresh batch
    loadWordBatch([]);
  };

  // Only effect for quiz status changes
  useEffect(() => {
    if (!equal(quizstatus, prevQuizStatus)) {
      tryAgain();
    }
  }, [quizstatus, prevQuizStatus]);

  // Initial load only on mount
  useEffect(() => {
    loadWordBatch([]);
  }, []);

  return {
    word,
    completed,
    totalMistakes,
    totalCorrect,
    quizDone,
    wordsNeedFilled,
    isLoading: isLoading && wordQueue.length === 0,
    quizType,
    equals,
    notEquals,
    tryAgain,
    settings: {
      traditional: languagesettings.traditional,
      wordSelection: quizsettings.wordSelection,
    },
  };
};
